import React, { useState } from "react";
import Div from "@jumbo/shared/Div";
import {
  Alert,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ASSET_CODINGPAL } from "../../../utils/constants/paths";
import { ROUTES } from "app/utils/constants/routeConstants";
import authServices from "../../../services/auth-services";
import { useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { Form, Formik, Field } from "formik";
import * as yup from "yup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { LoadingButton } from "@mui/lab";
import useMetaTitle from "app/hooks/useMetaTitle";
import { useTheme } from "@mui/material/styles";

const validationSchema = yup.object({
  login: yup.string("Enter your username").required("Username is required"),
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string("Enter your password")
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const Signup2 = () => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const { setAuthToken } = useJumboAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const getErrorMessage = (err) => {
    if (err.response?.status === 401) {
      return "Invalid credentials. Please try again.";
    }
    if (err.response?.status === 400) {
      return (
        err.response?.data?.title ||
        "Invalid input. Please check your details and try again."
      );
    }
    return (
      err.response?.data?.detail ||
      "An error occurred during sign-up. Please try again later."
    );
  };

  const onSignUp = async (login, email, password, setSubmitting) => {
    try {
      const data = await authServices.signUp({
        login,
        email,
        password,
        langKey: "en",
      });
      setAuthToken(data?.access_token);
      navigate(ROUTES.USER_VERIFY_ACCOUNT, {
        state: { source: "signup", email },
      });
    } catch (err) {
      setError(getErrorMessage(err));
      setOpen(true);
      setSubmitting(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  useMetaTitle({
    title: "Sign Up",
  });

  return (
    <Div
      sx={{
        width: 720,
        maxWidth: "100%",
        margin: "auto",
        p: 4,
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.up("md")]: {
          height: "calc(100vh - 130px)",
        },
      }}
    >
      <Card
        sx={{
          display: "flex",
          minWidth: 0,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <CardContent
          sx={{
            flex: "0 1 300px",
            position: "relative",
            background: "#222d45",
            backgroundSize: "cover",
            "&::after": {
              display: "inline-block",
              position: "absolute",
              content: `''`,
              inset: 0,
            },
          }}
        >
          <Div
            sx={{
              display: "flex",
              minWidth: 0,
              flex: 1,
              flexDirection: "column",
              color: "common.white",
              position: "relative",
              zIndex: 1,
              height: "100%",
            }}
          >
            <Div sx={{ mb: 2 }}>
              <Div
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  mt: "auto",
                  mb: 3,
                }}
              >
                <img
                  src={`${ASSET_CODINGPAL}/codingpal.jpg`}
                  alt="CodingPal"
                  width={60}
                  height={60}
                />
                <Typography
                  variant="h1"
                  color="white"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: 0,
                  }}
                >
                  CodingPal
                </Typography>
              </Div>
              <Typography
                variant={"h3"}
                color={"inherit"}
                fontWeight={500}
                mb={3}
              >
                Sign Up
              </Typography>
              <Typography variant={"body1"} mb={2}>
                By signing up, you can avail full features of CodingPal such as
                DSA Probelms on various topics, Code editor, Code Judge and Many
                more...
              </Typography>
            </Div>
          </Div>
        </CardContent>
        <CardContent sx={{ flex: 1, p: 4 }}>
          <Collapse in={open}>
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpen(false);
                    setError(null);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          </Collapse>
          <Formik
            validateOnChange={true}
            initialValues={{
              login: "",
              email: "",
              password: "",
            }}
            validationSchema={validationSchema}
            onSubmit={(data, { setSubmitting }) => {
              setSubmitting(true);
              onSignUp(data.login, data.email, data.password, setSubmitting);
            }}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form style={{ textAlign: "left" }} noValidate autoComplete="off">
                <Div sx={{ mt: 1, mb: 3 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    id="login"
                    name="login"
                    label="Username"
                    placeholder="Enter your username"
                    error={touched.login && !!errors.login}
                    helperText={touched.login && errors.login}
                  />
                </Div>
                <Div sx={{ mt: 1, mb: 3 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    placeholder="Enter your email"
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                </Div>
                <Div sx={{ mt: 1, mb: 2 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    id="password"
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a strong password"
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Div>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    mb: 2,
                    backgroundColor: "#222d45",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#222d45",
                    },
                  }}
                  loading={isSubmitting}
                >
                  Sign Up
                </LoadingButton>
              </Form>
            )}
          </Formik>
          <Div
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{
                color: "#222d45",
                textTransform: "none",
              }}
              onClick={() => {
                navigate(ROUTES.USER_LOGIN);
              }}
            >
              Sign In
            </Button>
            <Button
              sx={{
                color: "#475259",
                textTransform: "none",
              }}
              onClick={() => {
                navigate(ROUTES.USER_FORGOT_PASSWORD);
              }}
            >
              Forgot Password?
            </Button>
          </Div>
        </CardContent>
      </Card>
    </Div>
  );
};

export default Signup2;
